<script setup>
import Dates from '@/paks/js-dates'
import {onBeforeUnmount, onMounted, reactive, ref, watch} from 'vue'
import {Auth, Chat, State, getRoutePath, getSchemaModel} from '@/paks/vu-app'
import {Storage, can, delay, getModels, go, waitRender} from '@/paks/vu-app'

const page = reactive({
    alerts: [],
    cart: false,
    context: null,
    device: null,
    themeModes: {auto: '$auto', dark: '$dark', light: '$light'},
    features: State.config.features.nav,
    finished: null,
    hasWizard: false,
    hasMock: false,
    loading: false,
    loadingChat: false,
    logo: null,
    mock: State.app.mock,
    mini: false,
    name: null,
    ready: false,
    sleep: 0,
    showHelp: false,
    showBar: null,
    showLogin: null,
    showSidebar: null,
    social: State.auth.social,
    title: null,
})

//  Component references
const progress = ref(null)
const feedback = ref(null)

var helpPanel

onMounted(async () => {
    page.logo = State.app.logo

    if (page.features.help) {
        helpPanel = State.ref.vue._context.components['HelpPanel']
    }
    updateBar()
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
        if (State.app.themeMode == 'auto') {
            State.app.setThemeMode(e.matches ? 'dark' : 'light')
            State.app.setNeed('updateBoard')
        }
    })
    let query = State.app.location.query
    if (query.chat) {
        help()
        await Chat.open()
        State.app.noNeed('help')
    }

    watch(
        () => `${State.auth.accountName}-${State.auth.ready}`,
        async () => {
            if (State.auth.ready) {
                // Repeat here after login to get per-user persisted State.themeMode
                State.app.setThemeMode()
                if (State.config.name == 'builder') {
                    page.hasMock =
                        can('support') || State.auth.email.indexOf('@embedthis.com') > 0
                            ? true
                            : false
                    if (page.hasMock) {
                        page.mock = State.app.mock = Storage.getValue(`/navbar/mock`) || false
                    }
                }
                page.title = State.context.title || State.app.title || State.app.display.title
                if (State.config.profile == 'qa') {
                    page.title = 'EmbedThis Builder'
                }
                page.ready = State.auth.ready
                await getAccountName()
                daemon()
                checkWizards()
                checkCart()
                page.ready = true
            }
        },
        {immediate: true}
    )
    watch(
        () => State.app.needs.help,
        async (value) => {
            let query = State.app.location.query
            if (value == 'chat') {
                help()
                await Chat.open()
                State.app.noNeed('help')
            } else if (value) {
                help()
                State.app.noNeed('help')
            } else if (query.help || query.chat) {
                help()
            }
        }
    )
    watch(
        () => {
            return State.app.needs.updateBoard || State.app.needs.reload
        },
        async (value) => {
            if (State.auth.ready) {
                State.app.setThemeMode()
                updateBar()
                getAccountName()
                await getAlerts()
            }
        }
    )
    watch(
        () => State.app.needs.alertUpdate,
        async () => {
            await getAlerts()
        }
    )
    watch(
        () => State.cache.dashboard,
        () => {
            updateBar()
        },
        {deep: true, immediate: true}
    )

    watch(
        () => getRoutePath(),
        () => updateBar(),
        {deep: true}
    )
    watch(
        () => State.context,
        () => updateBar(),
        {deep: true}
    )
    watch(
        () => State.app.back,
        () => {
            updateBar()
        }
    )
    if (State.cache.plans) {
        watch(State.cache.plans, () => {
            checkCart()
        })
    }
    State.ref.router.afterEach((from, to) => {
        setTimeout(() => checkWizards(), 500)
    })
})

onBeforeUnmount(() => {
    page.finished = true
})

function updateBar() {
    page.showBar = page.features.navbar
    page.showSidebar = page.features.sidebar
    page.showLogin = page.features.login

    page.mini = State.app.mobile ? true : false
    page.title = State.app.title
    page.device = State.context.device || State.context.deviceId
}

async function daemon() {
    watch(
        () => State.app.lastAccess,
        async () => {
            //  On user click, if been asleep for more than 5 minutes, update alerts
            if (page.sleep > 5 * 60 * 1000) {
                await getAlerts()
            }
        }
    )
    while (!page.finished) {
        await getAlerts()
        let idle = Date.now() - State.app.lastAccess
        //  Increasing sleep time based on user activity. Minimum of 60 secs, max 6 hours.
        page.sleep = Math.max(60 * 1000, Math.min(6 * 3600 * 1000, idle / 2))
        await delay(page.sleep)
    }
}

async function getAlerts() {
    if (!page.ready || !State.auth.authorized || page.features.alerts == false) {
        page.alerts = []
        return
    }
    try {
        let Alert = getModels().Event || getModels().Alert
        if (Alert) {
            page.alerts = await Alert.find({}, {limit: 5})
            for (let alert of page.alerts) {
                alert.date = Dates.format(alert.date, State.config.formats.time)
                if (alert.severity == 'info') {
                    // alert.color = 'green'
                    alert.icon = '$info'
                } else if (alert.severity == 'warning') {
                    // alert.color = 'error'
                    alert.icon = '$warn'
                } else {
                    // alert.color = 'green'
                    alert.icon = '$info'
                }
            }
        }
    } catch (err) {}
}

async function getAccountName() {
    if (State.app.mock) {
        page.name = 'user@example.com'
    } else {
        page.name = State.auth.email || State.auth.username
    }
}

async function checkWizards() {
    await waitRender()
    let wizards = document.getElementsByClassName('vu-wizard')
    page.hasWizard = wizards.length
}

function toggleSidebar() {
    State.sidebar.toggle()
}

function changeDisplay() {
    let keys = Object.keys(page.themeModes)
    let index = keys.indexOf(State.app.themeMode)
    if (++index >= keys.length) {
        index = 0
    }
    State.app.setThemeMode(keys[index], true)
    State.app.setNeed('updateBoard')
}

function docClick() {
    window.open(page.features.doc, '_blank')
}

function cartClick() {
    go({name: 'subscription'})
}

function login() {
    go({name: 'login'})
}

async function logout() {
    await Auth.logout({redirect: true})
    go({name: 'login'})
}

async function help() {
    if (page.showHelp) {
        page.showHelp = false
        Chat.close()
    } else {
        openHelp()
    }
}

function openHelp() {
    page.showHelp = true
}

function closeHelp() {
    page.showHelp = false
    Chat.close()
}

function wizard() {
    State.app.launchWizard()
}

/*
async function designMode() {
    State.app.setNeed('design')
} */

async function tellus() {
    page.loadingChat = true
    await Chat.toggle()
    page.loadingChat = false
}

function mockClick() {
    State.app.mock = !State.app.mock
    page.mock = State.app.mock
    State.app.setNeed('updateBoard')
    Storage.setValue(`/navbar/mock`, page.mock)
}

function alertClick(alert) {
    let target
    if (State.config.builder) {
        target = '/automations/alerts'
    } else if (getSchemaModel('Event')) {
        target = '/events'
    } else {
        target = '/alerts'
    }
    if (alert) {
        go({url: target, query: {alert: alert.id}})
    } else {
        go(target)
    }
}

async function logoClick() {
    if (State.app?.display?.theme?.logoLink) {
        window.open(State.app?.display?.theme?.logoLink, '_blank')
    }
}

/*
    Invoked when user clicks the back button in the navbar
 */
async function back() {
    if (State.app.back) {
        go()
    }
    // Set to false rather than null to indicate to others that back button has bee pressed
    State.app.back = false
}

function checkCart() {
    page.cart = State.builder?.getCart()?.length
}
</script>

<template>
    <div class="nav-toolbar" v-show="page.ready" :class="`${page.mini ? 'mini' : ''}`">
        <div v-if="!page.showBar && page.showSidebar" class="floating-icon">
            <v-app-bar-nav-icon v-if="!State.app.back" class="toggle" @click.stop="toggleSidebar" />
            <v-btn v-else text icon>
                <v-icon icon="$lessThan" @click="back" />
            </v-btn>
        </div>

        <v-app-bar v-else-if="page.showBar" flat density="compact" class="pa-0">
            <template v-slot:prepend v-if="page.showSidebar">
                <v-app-bar-nav-icon
                    v-if="!State.app.back"
                    class="toggle"
                    @click.stop="toggleSidebar" />
                <v-btn v-else text icon>
                    <v-icon icon="$lessThan" @click="back" />
                </v-btn>
            </template>

            <img v-if="!page.mini" :src="page.logo" height="40" @click="logoClick" />

            <v-toolbar-title class="headline align-center">
                <a @click="go()">{{ page.title }}</a>
                <a class="ml-3" @click="go('/')">{{ page.device }}</a>
            </v-toolbar-title>

            <v-spacer />

            <v-toolbar-items v-if="page.showLogin" class="right-toolbar">
                <v-menu location="bottom" v-if="page.alerts?.length">
                    <template v-slot:activator="{props}">
                        <v-btn text icon v-bind="props">
                            <v-icon icon="$alert" color="white" />
                        </v-btn>
                    </template>
                    <v-list density="compact" min-width="400" class="nav-alerts">
                        <v-list-subheader class="header">
                            You have {{ page.alerts?.length || 0 }} alert notice{{
                                page.alerts?.length == 1 ? '' : 's'
                            }}
                        </v-list-subheader>
                        <v-list-item
                            v-for="(alert, i) in page.alerts"
                            :key="alert.id"
                            @click="alertClick(alert)">
                            <v-row>
                                <v-col cols="1">
                                    <v-icon
                                        :icon="alert.icon"
                                        :class="`severity-${alert.severity}`"
                                        size="x-large" />
                                </v-col>
                                <v-col cols="6">
                                    <span class="subject">
                                        {{ alert.subject }}
                                    </span>
                                </v-col>
                                <v-col cols="5">
                                    <v-icon icon="$clock" class="clock" size="x-large" />
                                    <span class="alert-date">
                                        {{ alert.date }}
                                    </span>
                                </v-col>
                            </v-row>
                        </v-list-item>
                        <v-spacer></v-spacer>
                        <v-list-subheader class="footer" @click="alertClick()">
                            View all alerts
                        </v-list-subheader>
                    </v-list>
                </v-menu>

                <div class="icons">
                    <v-btn icon v-if="page.hasWizard">
                        <v-icon icon="$wizard" id="launch-wizard" @click="wizard" />
                    </v-btn>
                    <v-btn icon v-if="page.features.doc && !State.app.mobile">
                        <v-icon icon="$bookshelf" @click="docClick" />
                    </v-btn>
                    <v-btn icon v-if="page.hasMock && !State.app.mobile">
                        <v-icon :icon="page.mock ? '$stop' : '$play'" @click="mockClick"></v-icon>
                    </v-btn>
                    <v-btn icon v-if="page.cart">
                        <v-icon icon="$cart" @click="cartClick" />
                    </v-btn>
                    <v-btn v-if="page.features.dark" icon @click="changeDisplay">
                        <v-icon :icon="page.themeModes[State.app.themeMode]" color="accent" />
                    </v-btn>
                </div>

                <v-btn text v-if="!page.ready && !State.app.mobile">
                    <a @click="login">Login</a>
                </v-btn>

                <v-menu class="pulldown" v-if="page.ready && !State.app.mobile">
                    <template v-slot:activator="{props}">
                        <v-btn text v-bind="props">{{ page.name }}</v-btn>
                    </template>
                    <v-list class="nav-menu">
                        <v-list-item to="/account/profile" v-if="page.features.profile">
                            <v-icon icon="$account" />
                            Profile
                        </v-list-item>
                        <v-list-item
                            to="/account/settings"
                            v-if="can('admin') && page.features.settings">
                            <v-icon icon="$cog" />
                            Account Settings
                        </v-list-item>
                        <v-list-item @click="logout">
                            <v-icon icon="$run" />
                            Logout
                        </v-list-item>
                    </v-list>
                </v-menu>
                <v-btn v-if="helpPanel" text class="help" @click="help">Help</v-btn>
            </v-toolbar-items>
        </v-app-bar>

        <vu-feedback ref="feedback" />
        <vu-progress
            class="progress"
            :class="State.cache.dashboard?.full || !page.showBar ? 'top' : ''"
            ref="progress" />

        <vu-panel
            v-if="helpPanel"
            v-model="page.showHelp"
            :isHelp="true"
            :classes="['help-panel']"
            :widths="['40%']"
            @close="closeHelp">
            <component v-bind:is="helpPanel" @close="closeHelp" />
        </vu-panel>

        <v-progress-circular
            v-if="page.loadingChat"
            class="loadingChat"
            color="primary"
            indeterminate
            :size="50" />
    </div>
</template>

<style lang="scss">
.nav-toolbar {
    height: 48px;
    &.hide {
        height: 0;
    }
    .v-toolbar__prepend {
        margin-inline: 0;
    }
    .v-toolbar__content {
        padding-right: 0;
    }
    .floating-icon {
        position: fixed;
        top: 0;
        left: 4px;
        z-index: 201;
    }
    .v-app-bar {
        position: fixed !important;
        background: rgb(var(--v-theme-navbar)) !important;
        color: rgb(var(--v-theme-on-navbar)) !important;
        box-shadow: var(
            --v-navbar-shadow,
            0 2px 4px -1px var(--v-shadow-key-umbra-opacity, #0003),
            0 4px 5px 0 var(--v-shadow-key-penumbra-opacity, #00000024),
            0 1px 10px 0 var(--v-shadow-key-ambient-opacity, #0000001f)
        ) !important;
        z-index: 201 !important;
        .headline {
            min-width: 130px;
            flex-grow: 10;
            @media (max-width: 640px) {
                text-align: center;
            }
        }
        a {
            text-decoration: none;
        }
        button {
            text-decoration: none;
        }
        .v-app-bar__nav-icon {
            margin-left: 0 !important;
        }
        .icons {
            height: inherit;
        }
        .right-toolbar {
            //  Above feedback
            z-index: 202;
        }
    }
    .nav-icon {
        position: absolute;
        top: 0;
        left: 0;
        height: 68px;
        width: 68px;
        z-index: 300;
        padding: 12px;
        background: none;
        opacity: 1;
        @media (max-width: 640px) {
            padding: 12px !important;
        }
        @media (prefers-color-scheme: dark) {
            opacity: 0.75;
        }
    }
    .nav-icon.full {
        margin-top: 10px;
        margin-left: 6px;
    }
    .nav-icon:not(.fixed) {
        background: none;
    }
    .progress {
        position: relative;
        top: 48px;
    }
    .progress.top {
        top: 0;
    }
}
.pulldown .v-overlay__content {
    border-radius: 0 0 8px 8px !important;
}
.nav-menu {
    padding: 10px 0 0 0 !important;
    border: solid 1px rgba(var(--v-theme-accent-darken-2), 0.5) !important;
    .v-icon {
        margin-right: 6px;
        width: 30px;
    }
    .items {
        font-size: 1rem;
    }
    hr {
        margin: 14px 0 14px 0;
    }
    .v-list-item {
        min-height: 40px !important;
        margin-right: 20px;
        width: 100%;
        .current {
            font-weight: bold;
        }
    }
    .v-list-item__content {
        padding: 6px 12px 6px 0 !important;
    }
    .v-list-item__icon {
        margin: 10px !important;
    }
}
.nav-toolbar.mini {
    height: 48px;
    .v-toolbar {
        height: 48px;
        .v-toolbar__content {
            height: 48px !important;
            font-size: 15px;
            .v-btn--size-default {
                font-size: 15px !important;
            }
            .v-btn--density-default {
                width: 48px !important;
            }
        }
        .v-toolbar-title {
            font-size: 15px !important;
            margin-left: 0;
            margin-inline-start: 0;
        }
        .headline {
            text-align: left !important;
        }
    }
    .nav-icon {
        height: 12px;
    }
}
.theme {
    margin-top: 12px !important;
    .v-input--switch__track {
        color: rgb(var(--v-theme-primary));
    }
}
.nav-alerts {
    background-color: rgb(var(--v-theme-background-lighten-1)) !important;
    border: solid 1px rgba(var(--v-theme-border-darken-1)) !important;
    padding-bottom: 0 !important;
    .header {
        font-weight: bold;
        font-size: 16px !important;
        margin: 0 0 10px 8px;
    }
    .footer {
        background-color: rgb(var(--v-theme-background-lighten-2));
        padding: 10px;
        padding-left: 20px;
        font-size: 14px !important;
        cursor: pointer;
    }
    .v-list-item {
        font-size: 12px !important;
        .v-list-item__content {
            margin: 0;
            display: inline-block;
            .v-icon {
                font-size: 1.8rem;
                margin-right: 8px;

                &.severity-undefined {
                    color: green;
                }
                &.severity-info {
                    color: green;
                }
                &.severity-warn {
                    color: orange;
                }
                &.severity-error {
                    color: red;
                }
                &.severity-critical {
                    color: red;
                }
            }
            .subject {
                display: inline-block;
                margin-top: 4px !important;
            }
            .v-icon.clock {
                display: inline-block;
                font-size: 16px;
            }
            .alert-date {
                margin-top: 4px !important;
                display: inline-block;
            }
        }
    }
}
.help-panel {
    padding: 48px 0 0 0;
    z-index: 200 !important;
    min-width: 600px;
    .panel.default {
        padding: 0 !important;
    }
}
</style>
